import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageSquareText, Sparkles } from "lucide-react";
import { Reservation } from "@/types/reservation";
import { Badge } from "@/components/ui/badge";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { Role } from "@/types/user";
import { PlatformRole } from "@/utils/accessControl";
import { getHttpStatus } from "@/utils/httpErrors";
import { formatRelativeTime } from "@/utils/dateUtils";
import { useMessagingChannelConfigs } from "@/components/pages/communication-channels/hooks/messaging-channel-configs.hooks";
import { CommunicationChannelKind } from "@/types/messaging-channel-config";
import {
  useClearGuestChannelPreference,
  useGuestChannelPreference,
  useUpsertGuestChannelPreference,
} from "@/components/pages/chats/hooks/guest-channel-preference.hooks";
import { GuestChannelPreferenceSelector } from "./GuestChannelPreferenceSelector";

const ALLOWED_ROLES: ReadonlyArray<Role | PlatformRole> = [
  Role.HOTEL_ADMIN,
  Role.FRONT_DESK,
  Role.TENANT_ADMIN,
  PlatformRole.SUPER_ADMIN,
];

interface GuestChannelPreferenceBookingInfoProps {
  booking: Reservation;
}

export const GuestChannelPreferenceBookingInfo: FC<
  GuestChannelPreferenceBookingInfoProps
> = ({ booking }) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();

  const role = userProfile?.role;
  const isAllowed =
    !!role && ALLOWED_ROLES.includes(role as Role | PlatformRole);

  const { guestId, hotelId } = booking;

  const {
    data: pref,
    isLoading: isLoadingPref,
    error: prefError,
  } = useGuestChannelPreference(
    isAllowed ? guestId : null,
    isAllowed ? hotelId : null,
  );

  const { data: configs, isLoading: isLoadingConfigs } =
    useMessagingChannelConfigs(isAllowed ? hotelId : null);

  const { mutate: upsert, isPending: isUpserting } =
    useUpsertGuestChannelPreference(guestId, hotelId);
  const { mutate: clear, isPending: isClearing } =
    useClearGuestChannelPreference(guestId, hotelId);

  if (!isAllowed) return null;

  const isLoading = isLoadingPref || isLoadingConfigs;
  const isMutating = isUpserting || isClearing;
  const preferred = pref?.preferred ?? null;
  const isKillSwitched = getHttpStatus(prefError) === 503;

  const enabledChannels = (configs ?? [])
    .filter((c) => c.enabled && c.providerAvailable)
    .map((c) => c.channel);
  const allChannelsOnMatrix = (configs ?? []).map((c) => c.channel);

  const handleError = (error: unknown) => {
    const status = getHttpStatus(error);
    if (status === 403) {
      toast.error(t("communicationChannels.guestPreference.toast.forbidden"));
      return;
    }
    toast.error(t("communicationChannels.guestPreference.toast.error"));
  };

  const handleSelect = (next: CommunicationChannelKind | "AUTO") => {
    if (next === "AUTO") {
      clear(undefined, {
        onSuccess: () =>
          toast.success(
            t("communicationChannels.guestPreference.toast.cleared"),
          ),
        onError: handleError,
      });
      return;
    }
    upsert(
      { preferred: next },
      {
        onSuccess: () =>
          toast.success(t("communicationChannels.guestPreference.toast.saved")),
        onError: handleError,
      },
    );
  };

  const renderTitlePill = () => {
    if (isLoading) {
      return (
        <Badge variant="new_secondary">
          {t("communicationChannels.guestPreference.pill.loading")}
        </Badge>
      );
    }
    if (preferred === null) {
      return (
        <Badge variant="verified">
          <Sparkles className="size-3" aria-hidden />
          {t("communicationChannels.guestPreference.pill.auto")}
        </Badge>
      );
    }
    return (
      <Badge variant="success">
        {t(`communicationChannels.channels.${preferred}.label`)}
      </Badge>
    );
  };

  const lastUpdatedLabel = pref?.updatedAt
    ? formatRelativeTime(pref.updatedAt)
    : null;

  return (
    <FoldableContainer
      titleNode={
        <span className="flex min-w-0 items-center gap-2">
          <MessageSquareText
            className="text-skyline-2 size-4 shrink-0"
            aria-hidden
          />
          <span className="truncate">
            {t("communicationChannels.guestPreference.title")}
          </span>
        </span>
      }
      extraNode={renderTitlePill()}
    >
      <div className="flex flex-col gap-3 pt-3">
        <p className="text-muted-foreground text-xs">
          {t("communicationChannels.guestPreference.helperText")}
        </p>

        <GuestChannelPreferenceSelector
          preferred={preferred}
          enabledChannels={enabledChannels}
          allChannelsOnMatrix={allChannelsOnMatrix}
          disabled={isLoading || isMutating || isKillSwitched}
          onChange={handleSelect}
        />

        <div className="text-muted-foreground flex flex-row flex-wrap items-center gap-2 text-xs">
          {lastUpdatedLabel && (
            <span>
              {t("communicationChannels.guestPreference.lastUpdated", {
                when: lastUpdatedLabel,
              })}
            </span>
          )}
          {preferred !== null && (
            <>
              {lastUpdatedLabel && <span aria-hidden>·</span>}
              <button
                type="button"
                onClick={() => handleSelect("AUTO")}
                disabled={isMutating || isKillSwitched}
                className="text-skyline-2 hover:underline disabled:opacity-50"
              >
                {t("communicationChannels.guestPreference.clear")}
              </button>
            </>
          )}
        </div>

        {isKillSwitched && (
          <p className="text-destructive text-xs">
            {t("communicationChannels.killSwitch")}
          </p>
        )}

        {!isLoading && !isKillSwitched && enabledChannels.length === 0 && (
          <p className="text-muted-foreground text-xs italic">
            {t("communicationChannels.guestPreference.emptyMatrix")}
          </p>
        )}
      </div>
    </FoldableContainer>
  );
};
